import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import {
  Camera, Upload, ArrowRight, Sparkles, Clock, CheckCircle2, DollarSign, Files,
  Zap, TrendingUp, BarChart3, AlertTriangle
} from 'lucide-react';
import LensUploadLightbox from '../../components/revq/LensUploadLightbox';
import { useAppMode } from '../../contexts/AppModeContext';

const RECENT_CLAIMS = [
  { id: 'CLM-2026-00845', patient: 'John Smith', dos: 'Mar 28, 2026', status: 'Submitted', est: '$450', color: '#3b82f6' },
  { id: 'CLM-2026-00844', patient: 'Maria Garcia', dos: 'Mar 27, 2026', status: 'Paid', est: '$1,250', color: '#22c55e' },
  { id: 'CLM-2026-00843', patient: 'Robert Johnson', dos: 'Mar 26, 2026', status: 'Pending', est: '$875', color: '#f59e0b' },
];

const STATUS_BADGE = {
  Submitted: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  Paid: { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  Pending: { bg: '#fffbeb', color: '#92400e', border: '#fde68a' },
};

const AUTOPILOT_LOG = [
  { time: '2:14 PM', patient: 'David Wilson', action: 'auto-submitted', score: 97, est: '$198' },
  { time: '2:08 PM', patient: 'Anna Rodriguez', action: 'auto-submitted', score: 95, est: '$165' },
  { time: '1:55 PM', patient: 'Michael Brown', action: 'auto-submitted', score: 93, est: '$420' },
  { time: '1:42 PM', patient: 'Lisa Park', action: 'moved to review', score: 62, est: '$95' },
  { time: '1:30 PM', patient: 'Robert Chen', action: 'auto-submitted', score: 91, est: '$342' },
];

const RevQAIClaims = () => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();
  const [showLens, setShowLens] = useState(false);

  return (
    <div className="space-y-6" data-testid="ai-claims-page">
      {/* Header with Enter Billing Focus */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-xl font-semibold" style={{ color: '#111' }}>AI Claims</h1>
            <span style={{ fontSize: 11, background: '#f0edff', color: '#5b21b6', borderRadius: 20, padding: '2px 8px', fontWeight: 500 }}>Echo-Powered</span>
          </div>
          <p style={{ fontSize: 13, color: '#888' }}>Upload or capture superbills for instant AI-driven claim processing</p>
        </div>
        <div className="text-right">
          <button
            onClick={() => navigate('/revq/billing-focus')}
            className="flex items-center gap-2 transition-colors hover:opacity-90"
            style={{ height: 40, padding: '0 20px', borderRadius: 8, background: '#111', color: '#fff', fontSize: 13, fontWeight: 500, border: 'none', cursor: 'pointer' }}
            data-testid="enter-billing-focus-btn"
          >
            <Zap style={{ width: 14, height: 14 }} />
            Enter Billing Focus
            <ArrowRight style={{ width: 14, height: 14, opacity: 0.6 }} />
          </button>
          <p style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>Distraction-free claim review and submission</p>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'This Month', value: '47', sub: 'claims submitted', icon: CheckCircle2, color: '#22c55e' },
          { label: 'First-Pass Rate', value: '97.2%', sub: 'passing on first try', icon: TrendingUp, color: '#3b82f6' },
          { label: 'Collected', value: '$18,430', sub: 'total collected', icon: DollarSign, color: '#16a34a' },
          { label: 'Avg Processing', value: '48s', sub: 'per claim', icon: Clock, color: '#6b5ce7' },
        ].map((s, i) => (
          <div key={i} style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: 10, padding: '14px 16px' }} data-testid={`stat-${i}`}>
            <div className="flex items-center justify-between mb-1">
              <span style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase' }}>{s.label}</span>
              <s.icon style={{ width: 14, height: 14, color: s.color }} />
            </div>
            <p style={{ fontSize: 20, fontWeight: 600, color: '#111' }}>{s.value}</p>
            <p style={{ fontSize: 10, color: '#888' }}>{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Quick Upload + Activity Log */}
      <div className="grid grid-cols-2 gap-4">
        {/* Quick Upload Zone */}
        <div>
          <p style={{ fontSize: 11, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Quick Upload</p>
          <div
            className="flex flex-col items-center text-center"
            style={{ background: '#fff', border: '1.5px dashed #d1d5db', borderRadius: 14, padding: '32px 24px' }}
            data-testid="upload-zone"
          >
            <Camera style={{ width: 24, height: 24, color: '#6b5ce7' }} />
            <p style={{ fontSize: 13, fontWeight: 500, color: '#111', marginTop: 10 }}>Upload a superbill or EOB</p>
            <p style={{ fontSize: 12, color: '#888', marginTop: 4 }}>AI extracts, validates, and prepares claims</p>
            <div className="flex items-center gap-2 mt-4">
              <button
                onClick={() => setShowLens(true)}
                className="flex items-center gap-1.5"
                style={{ height: 36, padding: '0 16px', borderRadius: 8, background: '#6b5ce7', color: '#fff', fontSize: 12, fontWeight: 500, border: 'none', cursor: 'pointer' }}
                data-testid="take-photo-btn"
              ><Camera className="h-3.5 w-3.5" /> Take a photo</button>
              <button
                onClick={() => setShowLens(true)}
                className="flex items-center gap-1.5"
                style={{ height: 36, padding: '0 16px', borderRadius: 8, background: '#fff', color: '#555', fontSize: 12, fontWeight: 500, border: '0.5px solid #d1d5db', cursor: 'pointer' }}
                data-testid="upload-file-btn"
              ><Upload className="h-3.5 w-3.5" /> Upload file</button>
            </div>
            <p style={{ fontSize: 10, color: '#bbb', marginTop: 8 }}>PDF, JPG, PNG, CSV · 3 credits per claim</p>
          </div>
        </div>

        {/* Autopilot Activity Log */}
        <div>
          <p style={{ fontSize: 11, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Autopilot Activity Log</p>
          <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: 14, padding: '12px' }}>
            <div className="space-y-1.5">
              {AUTOPILOT_LOG.map((entry, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2"
                  style={{
                    padding: '8px 10px',
                    borderRadius: 8,
                    background: entry.action === 'auto-submitted' ? '#f0fdf4' : '#fffbeb',
                    border: `0.5px solid ${entry.action === 'auto-submitted' ? '#bbf7d0' : '#fde68a'}`,
                  }}
                  data-testid={`activity-${i}`}
                >
                  <span style={{ fontSize: 10, color: '#aaa', width: 50, flexShrink: 0 }}>{entry.time}</span>
                  <span style={{ fontSize: 11, color: '#111', flex: 1, fontWeight: 500 }}>{entry.patient}</span>
                  <Badge style={{
                    fontSize: 9, border: 'none', padding: '1px 6px',
                    background: entry.action === 'auto-submitted' ? '#dcfce7' : '#fffbeb',
                    color: entry.action === 'auto-submitted' ? '#166534' : '#92400e',
                  }}>
                    {entry.action === 'auto-submitted' ? 'Submitted' : 'Review'}
                  </Badge>
                  <span style={{ fontSize: 10, color: '#22c55e', fontWeight: 500 }}>{entry.est}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Claims */}
      <div>
        <p style={{ fontSize: 11, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Recent claims</p>
        <div className="space-y-2">
          {RECENT_CLAIMS.map(claim => {
            const s = STATUS_BADGE[claim.status] || STATUS_BADGE.Submitted;
            return (
              <button
                key={claim.id}
                onClick={() => navigate('/revq/claims')}
                className="w-full flex items-center justify-between p-3 rounded-xl transition-all hover:bg-gray-50"
                style={{ background: '#fff', border: '0.5px solid #e8e8e8' }}
                data-testid={`recent-claim-${claim.id}`}
              >
                <div className="flex items-center gap-3">
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: claim.color }} />
                  <div className="text-left">
                    <p style={{ fontSize: 13, fontWeight: 500, color: '#111' }}>{claim.patient}</p>
                    <p style={{ fontSize: 11, color: '#aaa' }}>DOS {claim.dos}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span style={{
                    fontSize: 11, fontWeight: 500, borderRadius: 20, padding: '2px 8px',
                    background: s.bg, color: s.color, border: `0.5px solid ${s.border}`,
                  }}>{claim.status}</span>
                  <span style={{ fontSize: 12, fontWeight: 500, color: '#16a34a' }}>{claim.est}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-gray-400" />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Lens Upload Lightbox */}
      <LensUploadLightbox open={showLens} onOpenChange={setShowLens} />
    </div>
  );
};

export default RevQAIClaims;
